import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, CheckCircle } from "@phosphor-icons/react";

export default function Payment() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/config/payment").then((r) => setConfig(r.data));
  }, []);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings/payments/confirm", { booking_id: bookingId, reference, method: "mobile_money" });
      setDone(true);
      toast.success("Payment recorded. We'll verify within minutes.");
    } catch (err) {
      toast.error("Could not record payment");
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) return <div className="p-20 text-center font-mono text-xs">Loading payment details…</div>;

  return (
    <div className="max-w-[820px] mx-auto px-6 lg:px-10 py-16" data-testid="payment-page">
      <p className="label-eyebrow">Checkout</p>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-none mt-3 mb-10">Pay via Mobile Money</h1>

      {/* Receipt */}
      <div className="border-2 border-black p-8 font-mono text-sm">
        <div className="flex justify-between border-b border-black/20 pb-3 mb-4">
          <span>TWINSTARS / RECEIPT</span>
          <span>#{bookingId?.slice(-8).toUpperCase()}</span>
        </div>
        <Row label="Account name" value={config.mobile_money.name} onCopy={() => copy(config.mobile_money.name)} testid="mm-name" />
        <Row label="Network" value={config.mobile_money.network} onCopy={() => copy(config.mobile_money.network)} testid="mm-network" />
        <Row label="MoMo number" value={config.mobile_money.number} onCopy={() => copy(config.mobile_money.number)} testid="mm-number" />
        {config.mobile_money.pay_id && (
          <Row label="MoMo Pay ID" value={config.mobile_money.pay_id} onCopy={() => copy(config.mobile_money.pay_id)} testid="mm-pay-id" />
        )}
        <div className="border-t border-black/20 mt-4 pt-4 text-xs">
          1. Open your Mobile Money app.<br/>
          2. Transfer to the number above.<br/>
          3. Use booking <b>#{bookingId?.slice(-8).toUpperCase()}</b> as the reference.<br/>
          4. Paste the transaction ID below and submit.
        </div>
      </div>

      {/* Confirmation */}
      {done ? (
        <div className="mt-10 border border-black p-8 flex items-center gap-4">
          <CheckCircle size={32} weight="fill" />
          <div>
            <p className="font-display font-black text-2xl">Payment submitted</p>
            <p className="text-sm text-black/60">We'll verify your payment and update your booking shortly.</p>
          </div>
          <button onClick={() => nav("/dashboard")} className="btn-primary ml-auto" data-testid="payment-go-dashboard">My bookings</button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-5">
          <div>
            <label className="label-eyebrow block mb-2">Mobile Money transaction ID *</label>
            <input className="input-sharp" required value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. MP260524.1234.AB12345" data-testid="payment-reference" />
          </div>
          <button disabled={submitting || !reference} className="btn-primary" data-testid="payment-submit">
            {submitting ? "Recording…" : "I have paid — confirm"}
          </button>
          <p className="text-xs text-black/50">
            Or <Link to="/dashboard" className="underline">pay later</Link> from your dashboard.
          </p>
        </form>
      )}
    </div>
  );
}

function Row({ label, value, onCopy, testid }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-black/60">{label}</span>
      <div className="flex items-center gap-3">
        <span className="font-bold" data-testid={testid}>{value}</span>
        <button type="button" onClick={onCopy} className="text-black/60 hover:text-black" aria-label="copy"><Copy size={16} /></button>
      </div>
    </div>
  );
}
